import { useEffect, useRef, useState } from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  useAuth,
  useWalletFrontendConfig,
  paymentQueryKeys,
} from "@bw/shared/hooks";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { formatMoney, formatDate } from "@bw/shared/utils";
import type { SubscriptionResponse } from "@bw/shared/types/generated";
import { Page } from "../../../common/components/Page";
import { queryKeys } from "../../../common/query/queryClient";
import { useSubscription } from "../hooks/useSubscription";
import { useVisitBalance } from "../hooks/useVisitBalance";
import { useCancelSubscription } from "../hooks/useCancelSubscription";
import { useReactivateSubscription } from "../hooks/useReactivateSubscription";
import { useConfirmSubscription } from "../hooks/useConfirmSubscription";
import {
  MaterialConnectedElementsProvider,
  MaterialPaymentIntentAuthentication,
} from "../../../common/components/payment";
import { planTypeDisplay } from "../lib/planDisplay";
import {
  deriveSubscriptionView,
  deriveVisitBalanceView,
} from "../lib/subscriptionState";

/**
 * Membership detail + manage (`/me/memberships/:subscriptionId`). Follows the
 * settings + inline-confirm-destructive idiom (grouped key/value rows, no
 * card-in-card): the enriched subscription's facts, a visits_card pass's balance,
 * then the lifecycle actions gated on the HONEST derived state:
 *
 *   - active & !cancel_at_period_end → two-step inline "Cancel membership" (soft:
 *     access continues until current_period_end; the copy says so).
 *   - active &  cancel_at_period_end → an "Undo / Keep membership" (reactivate).
 *   - past_due → an honest "payment failed" notice pointing at the facility (there
 *     is no member self-serve retry endpoint yet — no dead control).
 *
 * There is NO "Renew now" button — renewal is automatic. Billing history reuses
 * /me/transactions.
 */
export function MembershipDetailPage() {
  const { subscriptionId: routeSubscriptionId } = useParams<{
    subscriptionId: string;
  }>();
  const subscriptionId = routeSubscriptionId?.toLowerCase();
  const { user } = useAuth();
  if (!user || !subscriptionId) return null;
  return (
    <MembershipDetailController
      key={`${user.uid}:${subscriptionId}`}
      actorId={user.uid}
      subscriptionId={subscriptionId}
    />
  );
}

function MembershipDetailController({
  actorId,
  subscriptionId,
}: {
  actorId: string;
  subscriptionId: string;
}) {
  const subQ = useSubscription(subscriptionId);
  const client = useQueryClient();
  const sub = subQ.data;
  const matches =
    sub?.subscription_id === subscriptionId && sub.user_id === actorId;
  const current =
    subQ.isSuccess &&
    subQ.isFetchedAfterMount &&
    subQ.error === null &&
    subQ.fetchStatus === "idle" &&
    matches;
  const [hasAuthorized, setHasAuthorized] = useState(false);
  useEffect(() => {
    if (current) setHasAuthorized(true);
  }, [current]);
  const isPass = sub?.plan_type === "visits_card";
  const balanceQ = useVisitBalance(subscriptionId, current && isPass);
  const balanceCurrent =
    balanceQ.isSuccess &&
    balanceQ.isFetchedAfterMount &&
    balanceQ.fetchStatus === "idle" &&
    balanceQ.error === null &&
    balanceQ.data.user_id === actorId &&
    balanceQ.data.subscription_id === subscriptionId;

  function canAct() {
    const live = client.getQueryState<SubscriptionResponse>([
      ...queryKeys.memberships.detail(subscriptionId),
      actorId,
    ]);
    return (
      current &&
      live?.status === "success" &&
      live.fetchStatus === "idle" &&
      !live.isInvalidated &&
      live.data?.subscription_id === subscriptionId &&
      live.data.user_id === actorId &&
      live.data === sub
    );
  }

  return (
    <Page
      title={current ? sub.plan_name?.trim() || "Membership" : "Membership"}
    >
      {subQ.fetchStatus === "paused" ? (
        <Alert severity="info" role="status">
          Waiting for a connection to refresh your membership.
        </Alert>
      ) : subQ.isError || (subQ.isSuccess && !matches) ? (
        <Alert severity="error">
          We couldn&apos;t load this membership. It may not exist.
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            sx={{ flexWrap: "wrap" }}
          >
            <Button
              color="inherit"
              size="small"
              onClick={() => void subQ.refetch()}
            >
              Retry
            </Button>
            <Button
              component={RouterLink}
              to="/me/memberships"
              color="inherit"
              size="small"
            >
              Back to my memberships
            </Button>
          </Stack>
        </Alert>
      ) : !current ? (
        <Stack
          direction="row"
          spacing={1.5}
          role="status"
          sx={{ alignItems: "center" }}
        >
          <CircularProgress size={20} />
          <Typography color="text.secondary">
            Loading your membership…
          </Typography>
        </Stack>
      ) : null}
      {sub && matches && (current || hasAuthorized) ? (
        <Box hidden={!current} inert={!current}>
          <MembershipDetail
            subscription={sub}
            canAct={canAct}
            refreshMembership={() => {
              void subQ.refetch();
              void client.refetchQueries({
                queryKey: paymentQueryKeys.walletFrontendConfig(),
                exact: true,
                type: "active",
              });
            }}
            balanceView={
              isPass && balanceCurrent
                ? deriveVisitBalanceView(balanceQ.data)
                : null
            }
          />
        </Box>
      ) : null}
    </Page>
  );
}

function MembershipDetail({
  subscription,
  balanceView,
  canAct,
  refreshMembership,
}: {
  subscription: SubscriptionResponse;
  balanceView: ReturnType<typeof deriveVisitBalanceView> | null;
  canAct: () => boolean;
  refreshMembership: () => void;
}) {
  const view = deriveSubscriptionView(subscription);
  const display = subscription.plan_type
    ? planTypeDisplay(subscription.plan_type, subscription.billing_interval)
    : null;
  const periodEnd = subscription.current_period_end;

  const cancel = useCancelSubscription(subscription.subscription_id);
  const reactivate = useReactivateSubscription(subscription.subscription_id);
  const confirmation = useConfirmSubscription();
  const [authentication, setAuthentication] = useState<{
    secret: string;
    account: string;
  } | null>(null);
  const [paymentError, setPaymentError] = useState(false);
  const paymentFired = useRef(false);
  const lifecycle = useRef(0);
  const latestCanAct = useRef(canAct);
  latestCanAct.current = canAct;
  useEffect(
    () => () => {
      lifecycle.current += 1;
    },
    [],
  );

  async function reconcile(): Promise<boolean> {
    if (!latestCanAct.current())
      throw new Error("Membership access must be refreshed");
    const generation = lifecycle.current;
    const result = await confirmation.mutateAsync(subscription.subscription_id);
    // The call's own settlement refresh may replace the passive DTO. It must
    // not dispose a correctly scoped response in this same private lifetime.
    // Presentation and new clicks still require the freshly authorized cache.
    if (generation !== lifecycle.current) return false;
    if (
      result.subscription_id !== subscription.subscription_id ||
      result.user_id !== subscription.user_id ||
      result.stripe_connect_account_id !==
        subscription.stripe_connect_account_id
    )
      throw new Error("Membership confirmation changed scope");
    const cleared = result.status === "active" && result.pending_state === null;
    if (cleared) setAuthentication(null);
    else if (
      result.requires_action &&
      result.client_secret &&
      result.stripe_connect_account_id
    )
      setAuthentication({
        secret: result.client_secret,
        account: result.stripe_connect_account_id,
      });
    return cleared;
  }

  async function completePayment() {
    if (
      paymentFired.current ||
      !latestCanAct.current() ||
      !subscription.available_actions.includes("complete_payment")
    )
      return;
    paymentFired.current = true;
    const generation = lifecycle.current;
    setPaymentError(false);
    try {
      await reconcile();
    } catch {
      if (generation === lifecycle.current) setPaymentError(true);
    } finally {
      paymentFired.current = false;
    }
  }

  const [confirmingCancel, setConfirmingCancel] = useState(false);
  // Synchronous single-fire guard (mirrors PaymentMethodsPage) — isPending only
  // flips on the next render, so two same-tick confirm clicks would both fire.
  const cancelFiredRef = useRef(false);
  const reactivateFiredRef = useRef(false);
  // a11y: move focus to the destructive Confirm control when the two-step
  // confirm appears, so keyboard/SR users land on the action they just revealed
  // instead of being stranded on the now-replaced "Cancel membership" trigger.
  const confirmCancelBtnRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (confirmingCancel) confirmCancelBtnRef.current?.focus();
  }, [confirmingCancel]);

  // Cancel is offered for an active (not-yet-cancelling) sub AND for a past_due
  // sub — a past_due member must be able to cancel to STOP the dunning cycle
  // (cancel_subscription accepts a past_due sub). A pending soft-cancel shows
  // Reactivate instead (canReactivate); terminal states show neither.
  const canCancel = subscription.available_actions.includes("cancel");

  const facilityName = subscription.facility_name?.trim() || null;

  const cancelCopy = periodEnd
    ? `You'll keep access until ${formatDate(periodEnd)}, then your membership cancels. You won't be charged again.`
    : `You'll keep access until the end of the current period, then your membership cancels. You won't be charged again.`;

  function handleConfirmCancel() {
    if (cancelFiredRef.current || !canCancel || !canAct()) return;
    cancelFiredRef.current = true;
    cancel.mutate(undefined, {
      onSuccess: () => {
        cancelFiredRef.current = false;
        setConfirmingCancel(false);
      },
      onError: () => {
        cancelFiredRef.current = false;
      },
    });
  }

  return (
    <Stack spacing={3}>
      {/* Grouped key/value facts — no card-in-card. */}
      <Box component="dl" sx={{ m: 0 }}>
        {facilityName ? (
          <MembershipFact label="Facility" value={facilityName} />
        ) : null}

        <MembershipFact
          label="Status"
          value={
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <Chip size="small" label={view.label} variant="outlined" />
              {view.detail ? (
                <Typography
                  component="span"
                  variant="body2"
                  color="text.secondary"
                >
                  {view.detail}
                </Typography>
              ) : null}
            </Stack>
          }
        />

        {subscription.price != null ? (
          <MembershipFact
            label="Price"
            value={
              <>
                {formatMoney(subscription.price)}
                {display?.priceSuffix ?? ""}
              </>
            }
          />
        ) : null}

        {/* Visits — only for a visits_card pass with a resolved balance. */}
        {balanceView ? (
          <MembershipFact
            label="Visits"
            value={
              <>
                {balanceView.label}
                {balanceView.expiryDetail ? (
                  <Typography
                    component="span"
                    variant="body2"
                    color="text.secondary"
                  >
                    {` · ${balanceView.expiryDetail}`}
                  </Typography>
                ) : null}
              </>
            }
          />
        ) : null}
      </Box>

      {subscription.available_actions.includes("complete_payment") &&
      !authentication ? (
        <Button
          size="small"
          variant="contained"
          onClick={() => void completePayment()}
          disabled={
            confirmation.isPending || !subscription.stripe_connect_account_id
          }
        >
          {confirmation.isPending ? "Confirming…" : "Complete payment"}
        </Button>
      ) : null}
      {paymentError ? (
        <Alert severity="error">
          We couldn&apos;t confirm this membership. Refresh or try again.
        </Alert>
      ) : null}
      {authentication && subscription.pending_state === "activation_pending" ? (
        <MembershipPaymentAuthentication
          secret={authentication.secret}
          account={authentication.account}
          onAuthenticated={async () => {
            if (!(await reconcile()))
              throw new Error(
                "Membership payment confirmation is still pending",
              );
          }}
        />
      ) : null}

      {/* past_due — honest, points at the facility (no self-serve retry). */}
      {view.needsFacilityAction ? (
        <Alert severity="warning">
          Your last payment failed. Please contact the facility to update your
          payment and keep your membership active.
        </Alert>
      ) : null}

      {/* Pending soft-cancel — offer to undo (reactivate). */}
      {view.canReactivate ? (
        <Stack spacing={1.5} role="status" sx={{ alignItems: "flex-start" }}>
          <Typography>
            This membership is set to cancel
            {periodEnd
              ? ` on ${formatDate(periodEnd)}`
              : " at the end of the period"}
            . You can keep it going.
          </Typography>
          <Button
            variant="contained"
            size="small"
            onClick={() => {
              if (
                !view.canReactivate ||
                !canAct() ||
                reactivateFiredRef.current
              )
                return;
              reactivateFiredRef.current = true;
              reactivate.mutate(undefined, {
                onSettled: () => {
                  reactivateFiredRef.current = false;
                },
              });
            }}
            disabled={reactivate.isPending}
          >
            {reactivate.isPending ? "Restoring…" : "Keep my membership"}
          </Button>
          {reactivate.isError ? (
            <Alert severity="error">
              We couldn&apos;t restore your membership. Please try again.
            </Alert>
          ) : null}
        </Stack>
      ) : null}

      {/* Cancel — two-step inline confirm, for an active (not-yet-cancelling)
            OR a past_due subscription. Soft cancel: access continues until
            period end and the member is not charged again (stops dunning). */}
      {canCancel ? (
        <Box>
          {!confirmingCancel ? (
            <Button
              color="error"
              variant="outlined"
              size="small"
              onClick={() => {
                if (!canAct()) return;
                cancel.reset();
                setConfirmingCancel(true);
              }}
            >
              Cancel membership
            </Button>
          ) : (
            <Stack spacing={1.5} role="group" sx={{ alignItems: "flex-start" }}>
              <Typography>{cancelCopy}</Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  ref={confirmCancelBtnRef}
                  color="error"
                  variant="contained"
                  size="small"
                  onClick={handleConfirmCancel}
                  disabled={cancel.isPending}
                >
                  {cancel.isPending ? "Cancelling…" : "Confirm cancel"}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setConfirmingCancel(false)}
                  disabled={cancel.isPending}
                >
                  Keep membership
                </Button>
              </Stack>
              {cancel.isError ? (
                <Alert severity="error">
                  We couldn&apos;t cancel your membership. Please try again.
                </Alert>
              ) : null}
            </Stack>
          )}
        </Box>
      ) : null}

      {/* Billing history reuses /me/transactions. Not a "renew" action. */}
      <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
        <Button size="small" onClick={refreshMembership}>
          Refresh
        </Button>
        <Button
          component={RouterLink}
          variant="text"
          size="small"
          to="/me/transactions"
        >
          Billing history
        </Button>
        <Button
          component={RouterLink}
          variant="text"
          size="small"
          to="/me/memberships"
        >
          All memberships
        </Button>
      </Stack>
    </Stack>
  );
}

function MembershipPaymentAuthentication({
  secret,
  account,
  onAuthenticated,
}: {
  secret: string;
  account: string;
  onAuthenticated: () => Promise<void>;
}) {
  const config = useWalletFrontendConfig();
  if (config.isError)
    return (
      <Alert severity="error">
        Payment authentication is unavailable. Refresh and try again.
      </Alert>
    );
  if (!config.data)
    return (
      <Typography role="status">Loading payment authentication…</Typography>
    );
  return (
    <MaterialConnectedElementsProvider
      config={{
        ...config.data,
        connected_account_id: account,
        stripe_account_id: account,
      }}
    >
      <MaterialPaymentIntentAuthentication
        clientSecret={secret}
        onAuthenticated={onAuthenticated}
        authenticationFailureMessage="We couldn't confirm this payment. Try again or review your recorded membership."
      />
    </MaterialConnectedElementsProvider>
  );
}

function MembershipFact({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={0.5}
      sx={{
        py: 1.25,
        borderBottom: 1,
        borderColor: "divider",
        justifyContent: "space-between",
        alignItems: { sm: "center" },
      }}
    >
      <Typography component="dt" color="text.secondary">
        {label}
      </Typography>
      <Typography component="dd" sx={{ m: 0, fontWeight: 600 }}>
        {value}
      </Typography>
    </Stack>
  );
}
